import SubmissionInformationStep from 'src/pages/Organization/public/Journal/RevisionSteps/SubmissionInformationStep';
import AuthorInformationStep from 'src/pages/Organization/public/Journal/RevisionSteps/AuthorInformationStep';
import SubmissionPreferencesStep from 'src/pages/Organization/public/Journal/RevisionSteps/SubmissionPreferencesStep';
import ReviewRevisedSubmissionStep from 'src/pages/Organization/public/Journal/RevisionSteps/ReviewRevisedSubmissionStep';
import RevisionUploadStep from 'src/pages/Organization/public/Journal/RevisionSteps/RevisionUploadStep';
import { useEffect, useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import type { FormFieldDefinition } from '@publishing-engine/domain/types/article';
import type { IGetSubmissionFieldsResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useSubmissionFields } from './useSubmissionFields';
import { getUnsubmittedReviewSections } from 'src/utils/unsubmittedReviewSections';
import { getRevisionUploadSectionLabel } from 'src/utils/revisionUploadSection';

export type StepComponent =
  | typeof RevisionUploadStep
  | typeof SubmissionInformationStep
  | typeof AuthorInformationStep
  | typeof SubmissionPreferencesStep
  | typeof ReviewRevisedSubmissionStep;

export type IStepConfig = {
  Component: StepComponent;
  configs: null;
  displayName: string | null;
  sections: string[];
};

const getSectionLabel = (
  fields: IGetSubmissionFieldsResponse['fields'],
  fieldName: FormFieldDefinition['name'],
  fallback?: string
) => {
  const field = fields[fieldName];
  if (!field) return null;
  const articleLanguage = fields['ArticleLanguage']?.value as
    | string
    | undefined;
  const isMainField =
    'isMainField' in field.formFieldDefinition.params &&
    !!field.formFieldDefinition.params.isMainField;

  const hasExternalDependency =
    (!!field.externalDependency?.path ||
      ('externalFieldDependency' in field.formFieldDefinition.params &&
        !!field.formFieldDefinition.params.externalFieldDependency?.path)) &&
    !isMainField;
  if (hasExternalDependency) return null;
  if (!!field.externalDependency?.path && articleLanguage === 'English')
    return null;

  if (fieldName === 'RecommendedReviewers') return 'Recommended Reviewers';
  if (fieldName === 'OpposedReviewers') return 'Opposed Reviewers';

  return (
    field.formFieldDefinition.params?.labels?.main || fallback || fieldName
  );
};

const uniqueSections = (sections: Array<string | null>) => [
  ...new Set(
    sections.filter(
      (section): section is string => !!section && section.trim().length > 0
    )
  )
];

export const useRevisionStepsConfigs = () => {
  const {
    fieldsResponse: { fields },
    workflowResponse: { workflowData },
    submissionContext
  } = useSubmissionData();
  const { checkFormFieldValueIsUpdated } = useSubmissionFields('submission');
  const [steps, setSteps] = useState<IStepConfig[]>([]);

  useEffect(() => {
    const isUnsubmitted = workflowData.currentState === 'unsubmitted';
    const consolidatedPdfSection = submissionContext.config
      .enableConsolidatedPdf
      ? ['Consolidated Submission File']
      : [];
    const revisionRequestSections = isUnsubmitted ? [] : ['Revision request'];
    const unsubmittedFeedbackSections = isUnsubmitted
      ? ['Submission Unsubmitted by Editors']
      : [];
    const unsubmittedReviewSections = getUnsubmittedReviewSections(
      checkFormFieldValueIsUpdated
    );
    const revisionUploadSectionLabel = getRevisionUploadSectionLabel({
      hasManuscript: !!fields['Manuscript'],
      hasTitlePage: !!fields['TitlePage']
    });
    const submissionInformationSections = uniqueSections([
      ...revisionRequestSections,
      getSectionLabel(fields, 'FocusIssue', 'Special Issue'),
      getSectionLabel(fields, 'Title'),
      getSectionLabel(fields, 'EnglishTitle'),
      getSectionLabel(fields, 'Subtitle'),
      getSectionLabel(fields, 'EnglishSubtitle'),
      getSectionLabel(fields, 'Abstract'),
      getSectionLabel(fields, 'EnglishAbstract'),
      getSectionLabel(fields, 'Keywords'),
      getSectionLabel(fields, 'EnglishKeywords'),
      getSectionLabel(fields, 'ArticleSubtype'),
      getSectionLabel(fields, 'ArticleLanguage'),
      getSectionLabel(fields, 'Category'),
      getSectionLabel(fields, 'CategoryOther'),
      getSectionLabel(fields, 'SourceFiles'),
      getSectionLabel(fields, 'Supplementary'),
      getSectionLabel(fields, 'AdditionalSupplementary'),
      getSectionLabel(fields, 'CoverLetter'),
      getSectionLabel(fields, 'StandardReportingGuidelines'),
      getSectionLabel(fields, 'Figures'),
      getSectionLabel(fields, 'Tables'),
      getSectionLabel(fields, 'NumberOfFigures'),
      getSectionLabel(fields, 'NumberOfColorFigures'),
      getSectionLabel(fields, 'NumberOfTables'),
      getSectionLabel(fields, 'NumberOfWords'),
      getSectionLabel(fields, 'GraphicalAbstractImage'),
      getSectionLabel(fields, 'GeneralClassifications')
    ]);

    const authorInformationSections = uniqueSections([
      ...revisionRequestSections,
      'All Authors',
      'Submission management'
    ]);

    const hasDatasetSection =
      getSectionLabel(fields, 'GeneratedDataset') ||
      getSectionLabel(fields, 'UtilizedDataset');

    const submissionPreferencesSections = uniqueSections([
      ...revisionRequestSections,
      getSectionLabel(fields, 'CommentForEditor'),
      getSectionLabel(fields, 'CoAuthorApproval'),
      getSectionLabel(fields, 'TransparentPeerReview'),
      getSectionLabel(fields, 'RecommendedReviewers'),
      getSectionLabel(fields, 'OpposedReviewers'),
      getSectionLabel(fields, 'DataAvailabilityStatement'),
      getSectionLabel(fields, 'ClinicalTrials'),
      getSectionLabel(fields, 'Funding', 'Funding Information'),
      getSectionLabel(fields, 'FundingGeneric'),
      getSectionLabel(fields, 'PreviouslySubmitted'),
      getSectionLabel(fields, 'ConflictOfInterestForAuthor'),
      getSectionLabel(fields, 'AITools'),
      getSectionLabel(fields, 'Preprint'),
      getSectionLabel(fields, 'SharedDataset'),
      hasDatasetSection ? 'Datasets Statement' : null,
      getSectionLabel(fields, 'EthicalApproval'),
      getSectionLabel(fields, 'EthicsApproval'),
      getSectionLabel(fields, 'CopyrightLicensingForm')
    ]);

    const updatedSteps: IStepConfig[] = [
      ...(!isUnsubmitted
        ? [
            {
              Component: RevisionUploadStep,
              configs: null,
              displayName: 'Revision upload & feedback response',
              sections: [
                'Revision request',
                ...(revisionUploadSectionLabel
                  ? [revisionUploadSectionLabel]
                  : []),
                'Your Response'
              ]
            }
          ]
        : []),
      {
        Component: SubmissionInformationStep,
        configs: null,
        displayName: 'Submission information',
        sections: isUnsubmitted
          ? uniqueSections([
              ...unsubmittedFeedbackSections,
              'Submission Files',
              ...submissionInformationSections
            ])
          : submissionInformationSections
      },
      {
        Component: AuthorInformationStep,
        configs: null,
        displayName: 'Author information',
        sections: [...unsubmittedFeedbackSections, ...authorInformationSections]
      },
      {
        Component: SubmissionPreferencesStep,
        configs: null,
        displayName: 'Submission preferences',
        sections: [
          ...unsubmittedFeedbackSections,
          ...submissionPreferencesSections
        ]
      },
      {
        Component: ReviewRevisedSubmissionStep,
        configs: null,
        displayName: isUnsubmitted
          ? 'Review submission'
          : 'Review revised submission',
        sections: isUnsubmitted
          ? [...unsubmittedReviewSections.sections, ...consolidatedPdfSection]
          : [
              'Revision request',
              'Revised Submission',
              ...consolidatedPdfSection
            ]
      }
    ];

    setSteps(updatedSteps);
  }, [
    fields,
    workflowData.currentState,
    checkFormFieldValueIsUpdated,
    submissionContext.config.enableConsolidatedPdf
  ]);

  return {
    steps
  };
};
